import { create } from 'zustand';
import type {
  CallCenterBootstrap,
  CallCenterSettings,
  CatalogItem,
  Customer,
  CustomerAddress,
} from '@matix/types';
import { roundMoney } from '@matix/utils';
import type { CallCenterLinePayload } from './api';

/**
 * The agent's draft order.
 *
 * DELIBERATELY NOT `useCart`. The till's cart carries returns, exchanges,
 * loyalty redemption, gift cards, parked-order snapshots, offline capture and a
 * branch that is implicitly the JWT's — none of which a phone order has, and
 * two of which (the implicit branch, the offline queue) would be actively wrong
 * here. Reusing it would mean either weakening the till's invariants or
 * threading a "call centre mode" flag through the most safety-critical store in
 * the app. This one is small enough to read in a sitting, and the till is
 * untouched.
 *
 * The MONEY is not authoritative here either. `totals()` exists so the agent
 * can read a number to the customer, and it mirrors the server's arithmetic
 * step for step (round-then-sum, at the tenant's currency precision) — but the
 * server recomputes on send and rejects a disagreement over a cent. That is the
 * design: the screen may be stale, the ledger may not.
 */

export interface CallCenterCartLine {
  /** Stable key for React, and what "the same line" means when merging. */
  key: string;
  itemId: number;
  name: string;
  nameAr: string | null;
  /** Unit price INCLUDING modifier deltas, matching the till's convention. */
  unitPrice: number;
  qty: number;
  notes: string | null;
  imageData: string | null;
  modifiers: Array<{
    option_id: number;
    group_id: number;
    group_name: string;
    name: string;
    name_ar?: string | null;
    price_delta: number;
  }>;
}

export interface CallCenterTotals {
  subtotal: number;
  discount: number;
  deliveryFee: number;
  taxValue: number;
  /**
   * The promotion/membership-EXCLUDED total. This is what goes on the wire as
   * `expected_total`, because the server verifies that field against its own
   * promotion-excluded recomputation — a client cannot know a promotion until
   * the server answers.
   */
  total: number;
  /** Promotions + membership benefits, as the server last priced them. */
  engineDiscount: number;
  engineBreakdown: Array<{ name: string; amount: number }>;
  /** A coupon's value. Client-known, so it counts toward `total` as well. */
  couponDiscount: number;
  /** What the customer actually pays. This is the number to read out loud. */
  amountDue: number;
}

interface CallCenterState {
  // ── Bootstrap ──────────────────────────────────────────────────────────
  bootstrap: CallCenterBootstrap | null;
  loadingBootstrap: boolean;
  setBootstrap: (b: CallCenterBootstrap) => void;
  setLoadingBootstrap: (v: boolean) => void;

  // ── Draft ──────────────────────────────────────────────────────────────
  branchId: number | null;
  customer: Customer | null;
  address: CustomerAddress | null;
  lines: CallCenterCartLine[];
  discount: number;
  deliveryFee: number;
  /**
   * When the caller wants the order, as a naive LOCAL `YYYY-MM-DDTHH:mm:00`
   * from the time picker. Null = "now" — the server stamps the receipt's
   * CustomerDeliverDate with the time it was sent, as it always has.
   */
  deliveryAt: string | null;
  note: string;
  driverId: number | null;
  prepaidPaymentTypeId: number | null;
  /** One UUID per draft, resent on retry. Cleared when the draft is cleared. */
  idempotencyKey: string;
  /**
   * Promotions + membership benefits, as the server last priced this basket.
   *
   * Kept on the draft rather than derived, because only the server can know
   * it: the rules, the scoping and the stacking all live there, and a
   * membership benefit also depends on how much of a monthly allowance the
   * customer has already spent elsewhere today. Refreshed by `useEngineQuote`
   * whenever the basket, the customer or the branch changes.
   */
  engineDiscount: number;
  engineBreakdown: Array<{ name: string; amount: number }>;
  quotePending: boolean;
  /**
   * A coupon code the agent typed. Priced by the server on every re-quote —
   * the client never decides what a coupon is worth.
   */
  couponCode: string;
  couponDiscount: number;
  /** Why the server refused the code, so the agent can read it out. */
  couponError: string | null;
  /**
   * A gift card read out over the phone. Must cover the order in full — see
   * the schema note on `gift_card`; a partial card is taken at hand-over.
   */
  giftCard: { number: string; pin: string };
  setEngineQuote: (q: {
    discount: number;
    breakdown: Array<{ name: string; amount: number }>;
    couponDiscount?: number;
    couponError?: string | null;
  }) => void;
  setQuotePending: (pending: boolean) => void;
  setCouponCode: (code: string) => void;
  setGiftCard: (card: { number: string; pin: string }) => void;

  setBranch: (branchId: number | null) => void;
  setCustomer: (customer: Customer | null) => void;
  setAddress: (address: CustomerAddress | null) => void;
  addItem: (
    item: CatalogItem,
    opts?: {
      qty?: number;
      unitPrice?: number;
      notes?: string | null;
      modifiers?: CallCenterCartLine['modifiers'];
    },
  ) => void;
  setQty: (key: string, qty: number) => void;
  setLinePrice: (key: string, price: number) => void;
  setLineNotes: (key: string, notes: string | null) => void;
  removeLine: (key: string) => void;
  setDiscount: (value: number) => void;
  setDeliveryFee: (value: number) => void;
  setDeliveryAt: (value: string | null) => void;
  setNote: (note: string) => void;
  setDriver: (driverId: number | null) => void;
  setPrepaid: (paymentTypeId: number | null) => void;
  clearDraft: () => void;
  /** Keeps the branch, drops everything else — "next caller". */
  newOrder: () => void;

  totals: () => CallCenterTotals;
  toPayload: () => CallCenterLinePayload[];
  settings: () => CallCenterSettings | null;
  precision: () => number;
}

function newKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `k${Date.now()}${Math.random().toString(16).slice(2)}`;
}

/**
 * Two lines are "the same" when the item, the price and the modifier SET all
 * match — the same rule the till uses. Notes deliberately do not merge: "no
 * onions" on one of two burgers is a different instruction from "no onions" on
 * both, and silently collapsing them sends the kitchen the wrong food.
 */
function mergeKey(
  itemId: number,
  unitPrice: number,
  modifiers: CallCenterCartLine['modifiers'],
  notes: string | null,
): string {
  const mods = [...modifiers]
    .map((m) => m.option_id)
    .sort((a, b) => a - b)
    .join('.');
  return `${itemId}|${unitPrice}|${mods}|${notes ?? ''}`;
}

const EMPTY_DRAFT = {
  customer: null,
  address: null,
  lines: [] as CallCenterCartLine[],
  discount: 0,
  deliveryFee: 0,
  deliveryAt: null,
  note: '',
  driverId: null,
  prepaidPaymentTypeId: null,
  // Cleared with the basket: a promotion priced for the previous caller must
  // never survive into the next one's total.
  engineDiscount: 0,
  engineBreakdown: [] as Array<{ name: string; amount: number }>,
  quotePending: false,
  couponCode: '',
  couponDiscount: 0,
  couponError: null as string | null,
  giftCard: { number: '', pin: '' },
};

export const useCallCenter = create<CallCenterState>((set, get) => ({
  bootstrap: null,
  loadingBootstrap: false,
  setBootstrap: (bootstrap) =>
    set((s) => ({
      bootstrap,
      // Open on the tenant's configured branch the first time, but never
      // reassign it under an agent who has already picked one mid-call.
      branchId: s.branchId ?? bootstrap.settings.defaultBranchId,
      deliveryFee: s.lines.length === 0 ? bootstrap.settings.defaultDeliveryFee : s.deliveryFee,
    })),
  setLoadingBootstrap: (loadingBootstrap) => set({ loadingBootstrap }),

  branchId: null,
  ...EMPTY_DRAFT,
  idempotencyKey: newKey(),

  setBranch: (branchId) =>
    set((s) => {
      // Changing branch mid-basket clears the lines. A branch menu is not a
      // superset of another's — silently carrying an item the new branch does
      // not sell produces an order the kitchen cannot cook, discovered by a
      // cook rather than by the agent who still has the customer on the line.
      if (s.branchId != null && branchId !== s.branchId && s.lines.length > 0) {
        return { branchId, lines: [], idempotencyKey: newKey() };
      }
      return { branchId };
    }),

  setCustomer: (customer) =>
    set({
      customer,
      // The address belongs to the customer; keeping a previous caller's
      // address attached to a new one is how food goes to the wrong door.
      address: customer?.addresses?.[0] ?? null,
    }),
  setAddress: (address) => set({ address }),

  addItem: (item, opts) =>
    set((s) => {
      const modifiers = opts?.modifiers ?? [];
      const base = opts?.unitPrice ?? item.selling_price ?? 0;
      const unitPrice = roundMoney(
        base + modifiers.reduce((sum, m) => sum + m.price_delta, 0),
        get().precision(),
      );
      const notes = opts?.notes ?? null;
      const qty = opts?.qty ?? 1;
      const key = mergeKey(item.id, unitPrice, modifiers, notes);

      const existing = s.lines.find((l) => l.key === key);
      if (existing) {
        return {
          lines: s.lines.map((l) => (l.key === key ? { ...l, qty: l.qty + qty } : l)),
        };
      }
      return {
        lines: [
          ...s.lines,
          {
            key,
            itemId: item.id,
            name: item.name ?? `#${item.id}`,
            nameAr: item.name_ar,
            unitPrice,
            qty,
            notes,
            imageData: item.image_data ?? null,
            modifiers,
          },
        ],
      };
    }),

  setQty: (key, qty) =>
    set((s) => ({
      lines:
        qty <= 0
          ? s.lines.filter((l) => l.key !== key)
          : s.lines.map((l) => (l.key === key ? { ...l, qty } : l)),
    })),

  setLinePrice: (key, price) =>
    set((s) => ({
      lines: s.lines.map((l) => (l.key === key ? { ...l, unitPrice: Math.max(0, price) } : l)),
    })),

  setLineNotes: (key, notes) =>
    set((s) => ({
      lines: s.lines.map((l) => (l.key === key ? { ...l, notes } : l)),
    })),

  removeLine: (key) => set((s) => ({ lines: s.lines.filter((l) => l.key !== key) })),

  setEngineQuote: ({ discount, breakdown, couponDiscount, couponError }) =>
    set({
      engineDiscount: Math.max(0, discount),
      engineBreakdown: breakdown,
      ...(couponDiscount !== undefined ? { couponDiscount: Math.max(0, couponDiscount) } : {}),
      ...(couponError !== undefined ? { couponError } : {}),
    }),
  setQuotePending: (quotePending) => set({ quotePending }),
  // Clearing the code clears its value immediately rather than waiting for the
  // next quote — a discount left on screen for a coupon that is no longer
  // there is a number the agent would read out and the order would refuse.
  setCouponCode: (couponCode) =>
    set(couponCode.trim() ? { couponCode } : { couponCode, couponDiscount: 0, couponError: null }),
  setGiftCard: (giftCard) => set({ giftCard }),

  setDiscount: (discount) => set({ discount: Math.max(0, discount) }),
  setDeliveryFee: (deliveryFee) => set({ deliveryFee: Math.max(0, deliveryFee) }),
  setDeliveryAt: (deliveryAt) => set({ deliveryAt }),
  setNote: (note) => set({ note }),
  setDriver: (driverId) => set({ driverId }),
  setPrepaid: (prepaidPaymentTypeId) => set({ prepaidPaymentTypeId }),

  clearDraft: () => set({ ...EMPTY_DRAFT, idempotencyKey: newKey() }),

  newOrder: () =>
    set((s) => ({
      ...EMPTY_DRAFT,
      // A fresh key, or a retry of the PREVIOUS order would dedupe against it
      // and return that receipt instead of writing this one.
      idempotencyKey: newKey(),
      deliveryFee: s.bootstrap?.settings.defaultDeliveryFee ?? 0,
    })),

  /**
   * Round-then-sum, at the tenant's currency precision, taxing
   * (subtotal − discount + delivery). Every step mirrors
   * `computeCallCenterTotals` on the server; a divergence here shows up as a
   * TOTALS_MISMATCH on send rather than as a wrong number on a receipt.
   */
  totals: () => {
    const s = get();
    const p = s.precision();
    const round = (n: number): number => roundMoney(n, p);

    const subtotal = round(
      s.lines.reduce((sum, l) => sum + round(l.qty * l.unitPrice), 0),
    );
    const discount = round(Math.max(0, s.discount));
    const deliveryFee = round(Math.max(0, s.deliveryFee));
    const taxPercentage = s.bootstrap?.taxPercentage ?? 0;
    // A coupon IS client-known (the agent typed the code), so unlike the engine
    // discount it belongs in `total` — which is what the server verifies
    // `expected_total` against. Both lower the taxable base.
    const couponDiscount = round(
      Math.min(Math.max(0, s.couponDiscount), Math.max(0, subtotal - discount)),
    );
    const taxableBase = subtotal - discount - couponDiscount + deliveryFee;
    const taxValue = round((taxableBase * taxPercentage) / 100);
    const total = round(taxableBase + taxValue);

    // Capped at what is left, mirroring the server's basket floor, so a stale
    // quote can never drive the order negative between refreshes.
    const engineDiscount = round(
      Math.min(
        Math.max(0, s.engineDiscount),
        Math.max(0, subtotal - discount - couponDiscount),
      ),
    );
    // Recomputed, not subtracted: the engine discount lowers the taxable base.
    const dueBase = subtotal - discount - couponDiscount - engineDiscount + deliveryFee;
    const amountDue = round(dueBase + round((dueBase * taxPercentage) / 100));

    return {
      subtotal,
      discount,
      deliveryFee,
      taxValue,
      total,
      engineDiscount,
      engineBreakdown: s.engineBreakdown,
      couponDiscount,
      amountDue,
    };
  },

  toPayload: () =>
    get().lines.map((l) => ({
      item_id: l.itemId,
      qty: l.qty,
      price: l.unitPrice,
      ...(l.notes ? { notes: l.notes } : {}),
      ...(l.modifiers.length > 0 ? { modifiers: l.modifiers } : {}),
    })),

  settings: () => get().bootstrap?.settings ?? null,
  precision: () => get().bootstrap?.currency.precision ?? 2,
}));
